import { useEffect } from 'react'
import { FilterStore } from 'components/Filters/FilterStore'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'

/**
 * Custom hook that automatically enhances a filter store to clear sorting when search is applied.
 *
 * @param filterStore - The filter store instance for the table
 * @param tableKey - The unique key for the table's sorting store (optional)
 */
export function useSearchWithSortReset(
  filterStore: FilterStore,
  tableKey?: string
) {
  const sortingStore = tableKey
    ? dataTableSortingStoreRepo.getStore(tableKey)
    : null
  const { setSorting } = sortingStore?.() ?? { setSorting: () => {} }

  useEffect(() => {
    // Only enhance if we have a valid tableKey and sorting store
    if (!tableKey || !sortingStore) return

    const store = filterStore.getState()

    // Store the original setSearch method
    const originalSetSearch = store.setSearch

    // Create an enhanced setSearch that clears sorting when search has a value
    const enhancedSetSearch = (
      key: string,
      value: string,
      label: string,
      elastic?: boolean
    ) => {
      // Call the original setSearch method
      originalSetSearch(key, value, label, elastic)

      // Clear sorting if search has a non-empty value
      if (value && value.trim() !== '') {
        setSorting([])
      }
    }

    // Enhance the store with the new setSearch method
    filterStore.setState({ setSearch: enhancedSetSearch })

    // Cleanup: restore original setSearch when component unmounts
    return () => {
      filterStore.setState({ setSearch: originalSetSearch })
    }
  }, [filterStore, setSorting, tableKey, sortingStore])

  return {
    // For backward compatibility, still provide these for custom implementations
    clearSortingOnSearch: (searchValue: string) => {
      if (searchValue && searchValue.trim() !== '' && setSorting) {
        setSorting([])
      }
    },
  }
}
